import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { dateToLocalISO } from "../utils/date";
import { faClipboardList, faCommentDots, faTrashCan, faCheck, faWandMagicSparkles, faSpinner, faLightbulb, faClockRotateLeft, faChevronDown, faChevronUp, faHistory, faTimes, faFlag, faDumbbell, faAppleWhole, faCamera, faCalendarCheck, faUser, faPersonRunning, faLocationDot, faPaperPlane, faBellSlash } from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./TeacherAnalyticsModal.module.css";
import ModalHeader from "./ModalHeader";
import SessionDetailModal from "./SessionDetailModal";
import OrderAdherenceSection from "./OrderAdherenceSection";
import NutritionModal from "./NutritionModal";
import BodyProgressModal from "./BodyProgressModal";
import WorkoutHistoryModal from "./WorkoutHistoryModal";
import SendPushModal from "./SendPushModal";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  Filler,
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";
import { themeColors, getCssVar } from "../utils/theme";
import { getUserLevel } from "../utils/levels";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { useHistoryBackClose } from "../hooks/useHistoryBackClose";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

ChartJS.register(
  CategoryScale, LinearScale, PointElement, LineElement,
  BarElement, Title, Tooltip, Legend, ArcElement, Filler
);

const WEEKDAY_LABELS = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sab", "Dom"];
const TEAL = themeColors.blue;
const AMBER  = "#f59e0b";
const BLUE   = "#63b3ed";
const VIOLET = "#9f77dd";

function getChartDefaults() {
  const tickColor = getComputedStyle(document.documentElement).getPropertyValue("--text-0-50").trim() || "rgba(120,120,120,0.7)";
  const gridColor = getComputedStyle(document.documentElement).getPropertyValue("--border-0-06").trim() || "rgba(200,200,200,0.08)";
  return {
    responsive: true,
    maintainAspectRatio: false,
    layout: { padding: { top: 24 } },
    plugins: { legend: { display: false } },
    scales: {
      x: { ticks: { color: tickColor, font: { size: 11 } }, grid: { color: gridColor } },
      y: { ticks: { color: tickColor, font: { size: 11 } }, grid: { color: gridColor } },
    },
  };
}

function fmtMin(m) {
  if (!m) return "-";
  const h = Math.floor(m / 60), min = m % 60;
  if (h === 0) return `${min}min`;
  if (min === 0) return `${h}h`;
  return `${h}h ${min}min`;
}

function fmtDate(iso) {
  if (!iso) return "-";
  const [y, mo, d] = iso.split("-");
  return `${d}/${mo}/${y.slice(2)}`;
}

function Initials({ name, size = 44 }) {
  const initials = (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
  return (
    <div className={styles.avatar} style={{ width: size, height: size, fontSize: size * 0.38 }}>
      {initials}
    </div>
  );
}

// ── Activity Heatmap ──────────────────────────────────────────────────────────
function ActivityHeatmap({ heatmap = [] }) {
  if (!heatmap.length) return null;
  const byDate = {};
  heatmap.forEach((d) => { byDate[d.date] = d.count; });
  const today = new Date();
  const todayStr = dateToLocalISO(today);
  const dow = (today.getDay() + 6) % 7;
  const monday = new Date(today); monday.setDate(today.getDate() - dow);
  const startMonday = new Date(monday); startMonday.setDate(monday.getDate() - 12 * 7);
  const weeks = [];
  for (let w = 0; w < 13; w++) {
    const days = [];
    for (let d = 0; d < 7; d++) {
      const cur = new Date(startMonday); cur.setDate(startMonday.getDate() + w * 7 + d);
      const iso = dateToLocalISO(cur);
      days.push({ date: iso, count: byDate[iso] || 0, isToday: iso === todayStr });
    }
    weeks.push(days);
  }
  const maxCount = Math.max(1, ...heatmap.map((d) => d.count));
  function cellColor(count) {
    if (count === 0) return "var(--border-0-04)";
    const alpha = 0.25 + Math.min(1, count / maxCount) * 0.65;
    return `rgba(${getCssVar("--accent-rgb","29,158,117")},${alpha.toFixed(2)})`;
  }
  const rawMonthLabels = weeks.map((days) => {
    const [y, m, d] = days[0].date.split("-");
    return new Date(+y, +m - 1, +d).toLocaleDateString("pt-BR", { month: "short" });
  });
  // Mostra o rótulo só na primeira semana de cada mês — com 14px por coluna,
  // repetir o mesmo texto em colunas vizinhas faria elas se sobreporem.
  const monthLabels = rawMonthLabels.map((label, i) => (i > 0 && label === rawMonthLabels[i - 1]) ? "" : label);
  return (
    <div className={styles.heatmapWrap}>
      <div className={styles.heatmapMonths}>
        {weeks.map((_, i) => <span key={i} className={styles.heatmapMonth}>{monthLabels[i]}</span>)}
      </div>
      <div className={styles.heatmapGrid}>
        <div className={styles.heatmapDayLabels}>
          {WEEKDAY_LABELS.map((l, i) => <span key={i} className={styles.heatmapDayLabel}>{l}</span>)}
        </div>
        <div className={styles.heatmapCols}>
          {weeks.map((days, wi) => (
            <div key={wi} className={styles.heatmapCol}>
              {days.map((day) => (
                <div key={day.date}
                  className={`${styles.heatmapCell} ${day.isToday ? styles.heatmapToday : ""}`}
                  style={{ background: cellColor(day.count) }}
                  title={`${fmtDate(day.date)} - ${day.count} treino${day.count !== 1 ? "s" : ""}`}
                />
              ))}
            </div>
          ))}
        </div>
      </div>
      <div className={styles.heatmapLegend}>
        <span>Menos</span>
        {[0, 0.3, 0.6, 0.8, 1].map((v, i) => (
          <div key={i} className={styles.heatmapLegendCell} style={{ background: v === 0 ? "var(--border-0-04)" : `rgba(${getCssVar("--accent-rgb","29,158,117")},${(0.25 + v * 0.65).toFixed(2)})` }} />
        ))}
        <span>Mais</span>
      </div>
    </div>
  );
}

// ── Profile Banner ────────────────────────────────────────────────────────────
function ProfileBanner({ profile, onSendPush }) {
  if (!profile) return null;
  const { student, period } = profile;
  const level = getUserLevel(student.total_points_all_time);
  return (
    <div className={styles.profileBanner} style={{ borderColor: level.color + "44" }}>
      <div className={styles.profileBannerLeft}>
        <Initials name={student.name} size={52} />
        <div>
          <div className={styles.profileName}>{student.name}</div>
          <div className={styles.profileMeta}>
            <span className={styles.levelBadge} style={{ background: level.color + "22", color: level.color, borderColor: level.color + "44" }}>
              <FontAwesomeIcon icon={level.icon} /> {level.tier}
            </span>
            <span className={styles.profilePts}>{student.total_points_all_time} pts</span>
          </div>
        </div>
        {onSendPush && (
          student.push_devices > 0 ? (
            <button type="button" className={styles.sendPushBtn} onClick={onSendPush} data-tooltip="Enviar uma notificação avulsa pro celular do aluno">
              <FontAwesomeIcon icon={faPaperPlane} /> Enviar notificação
            </button>
          ) : (
            <button
              type="button"
              className={`${styles.sendPushBtn} ${styles.sendPushBtnDisabled}`}
              disabled
              data-tooltip="Este aluno não tem o app instalado com notificações ativas - peça pra ele instalar pra poder falar com ele por aqui"
            >
              <FontAwesomeIcon icon={faBellSlash} /> Aluno sem notificações - peça pra instalar o app
            </button>
          )
        )}
      </div>
      <div className={styles.profileStats}>
        <div className={styles.profileStat} data-tooltip="Semanas consecutivas com pelo menos 1 treino registrado">
          <span className={styles.profileStatVal}>{period.streak_weeks}</span>
          <span className={styles.profileStatLbl}>semanas seguidas</span>
        </div>
        <div className={styles.profileStat} data-tooltip="Dias corridos desde o último treino registrado">
          <span className={styles.profileStatVal} style={{ color: period.days_since_last > 7 ? "#ff6b6b" : "var(--feedback-success)" }}>
            {period.days_since_last ?? "-"}
          </span>
          <span className={styles.profileStatLbl}>dias sem treinar</span>
        </div>
        <div className={styles.profileStat} data-tooltip="Sessões realizadas ÷ (meta semanal × semanas no período)">
          <span className={styles.profileStatVal}>{Math.round(Math.min(period.adherence, 1) * 100)}%</span>
          <span className={styles.profileStatLbl}>aderência</span>
        </div>
        <div className={styles.profileStat} data-tooltip="% de semanas em que o aluno atingiu ou superou a meta de treinos por semana">
          <span className={styles.profileStatVal}>{Math.round(period.goal_hit_rate * 100)}%</span>
          <span className={styles.profileStatLbl}>semanas na meta</span>
        </div>
      </div>
    </div>
  );
}

// ── KPI Grid ─────────────────────────────────────────────────────────────────
function KpiGrid({ period, bodyProgressEntries = [], measurements = [] }) {
  if (!period) return null;

  // Peso atual: Fotos de Progresso têm prioridade sobre Medidas Corporais (mesma
  // regra usada em BodyMeasurementsPanel) — evita depender de UserExercise.weight,
  // que é um campo opcional e pouco confiável do check-in de treino.
  const latestBP = bodyProgressEntries[0] || null;
  const latestM  = measurements[0] || null;

  let weightValue = "-";
  let weightUnit = "sem dados";
  let wcColor = "var(--text-0-50)";

  if (latestBP) {
    weightValue = `${latestBP.weight_kg} kg`;
    const prevBP = bodyProgressEntries[1] || null;
    const wc = prevBP ? +(latestBP.weight_kg - prevBP.weight_kg).toFixed(1) : null;
    weightUnit = wc != null ? (wc >= 0 ? `+${wc} kg` : `${wc} kg`) : `último reg. ${fmtDate(latestBP.recorded_at)}`;
    wcColor = wc < 0 ? TEAL : wc > 0 ? "#ff6b6b" : "var(--text-0-50)";
  } else if (latestM && latestM.weight_kg != null) {
    weightValue = `${latestM.weight_kg} kg`;
    const prevM = measurements[1] || null;
    const wc = (prevM && prevM.weight_kg != null) ? +(latestM.weight_kg - prevM.weight_kg).toFixed(1) : null;
    weightUnit = wc != null ? (wc >= 0 ? `+${wc} kg` : `${wc} kg`) : (latestM.date ? `em ${fmtDate(latestM.date)}` : "sem dados");
    wcColor = wc < 0 ? TEAL : wc > 0 ? "#ff6b6b" : "var(--text-0-50)";
  }

  const cards = [
    { label: "Treinos",      value: period.workouts,                              unit: "no periodo",    color: TEAL,   tooltip: "Total de sessões de treino concluídas no período selecionado" },
    { label: "Volume total", value: fmtMin(period.total_minutes),                 unit: "de treino",     color: BLUE,   tooltip: "Soma da duração de todas as sessões de treino no período" },
    { label: "Média/sessão", value: fmtMin(period.avg_duration),                  unit: "por treino",    color: VIOLET, tooltip: "Duração média por sessão de treino concluída" },
    { label: "Cadência",     value: (period.avg_weekly_cadence||0).toFixed(1),    unit: "treinos/sem",   color: AMBER,  tooltip: "Média de treinos por semana no período selecionado" },
    { label: "Peso atual",   value: weightValue, unit: weightUnit, color: wcColor,
      tooltip: "Peso mais recente - prioriza Fotos de Progresso; sem fotos, usa Medidas Corporais" },
    { label: "Meta atingida", value: `${period.goal_hit_weeks}/${period.total_weeks}`, unit: "semanas na meta", color: TEAL, tooltip: "Semanas em que o aluno completou a meta de treinos por semana" },
  ];
  return (
    <div className={styles.kpiGrid}>
      {cards.map((c) => (
        <div key={c.label} className={styles.kpiCard2} data-tooltip={c.tooltip}>
          <div className={styles.kpiCard2Val} style={{ color: c.color }}>{c.value}</div>
          <div className={styles.kpiCard2Label}>{c.label}</div>
          <div className={styles.kpiCard2Unit}>{c.unit}</div>
        </div>
      ))}
    </div>
  );
}

// ── Student Diet Panel ────────────────────────────────────────────────────────

function effectiveG(item) {
  const qty = item.quantity_g || 0;
  if (item.unit === "ml") return qty * (item.food?.density_g_per_ml ?? 1.0);
  return qty;
}

function calcDietMacros(meals, weightKg) {
  let pa = 0, pv = 0, carbs = 0, fiber = 0, fat = 0;
  for (const meal of (meals || [])) {
    for (const item of (meal.items || [])) {
      if (!item.food) continue;
      const r = effectiveG(item) / 100;
      pa    += (item.food.protein_animal_g || 0) * r;
      pv    += (item.food.protein_veg_g    || 0) * r;
      carbs += (item.food.carbs_g          || 0) * r;
      fiber += (item.food.fiber_g          || 0) * r;
      fat   += (item.food.fat_g            || 0) * r;
    }
  }
  const pt = pa + pv;
  const kcalP = pt * 4, kcalC = carbs * 4, kcalF = fat * 9;
  const total = kcalP + kcalC + kcalF;
  const pct = (v) => total > 0 ? (v / total * 100).toFixed(1) : "0.0";
  const gkg = (v) => weightKg ? (v / weightKg).toFixed(1) : "-";
  return {
    protein_total:  { g: pt.toFixed(0),    pct: pct(kcalP), kcal: Math.round(kcalP), gkg: gkg(pt)    },
    protein_animal: { g: pa.toFixed(0) },
    protein_veg:    { g: pv.toFixed(0) },
    carbs:          { g: carbs.toFixed(0), pct: pct(kcalC), kcal: Math.round(kcalC), gkg: gkg(carbs) },
    fiber:          { g: fiber.toFixed(0) },
    fat:            { g: fat.toFixed(0),   pct: pct(kcalF), kcal: Math.round(kcalF), gkg: gkg(fat)   },
    kcal_total:     Math.round(total),
  };
}

function StudentDietPanel({ studentId, onOpenDiet }) {
  const [diet, setDiet]       = useState(null);   // null=loading, false=nenhuma
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true);
    setDiet(null);
    api.get(`/nutrition/diets?user_id=${studentId}`)
      .then((r) => { if (!cancelled) setDiet(r.data?.[0] ?? false); })
      .catch(() => { if (!cancelled) setDiet(false); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  const hdr  = { backgroundColor: "var(--surface-secondary)", color: "var(--text-muted)", textAlign: "center", padding: "5px 8px", fontSize: "12px", fontWeight: "600", border: "1px solid #3a3a3a" };
  const lbl  = { padding: "5px 10px", fontWeight: "700", fontSize: "12px", border: "1px solid #333", color: "var(--text-primary)", backgroundColor: "var(--surface-secondary)", whiteSpace: "nowrap" };
  const sub  = { ...lbl, fontWeight: "400", paddingLeft: "20px", color: "var(--text-muted)" };
  const cell = { padding: "5px 8px", textAlign: "center", fontSize: "12px", border: "1px solid #333", color: "var(--text-muted)", backgroundColor: "var(--surface-primary)" };
  const bold = { ...cell, fontWeight: "700", color: "var(--text-primary)", backgroundColor: "var(--surface-secondary)" };

  const GOAL_LABELS = { cutting: "Cutting", manutencao: "Manutenção", bulking: "Bulking", saude: "Saúde" };

  return (
    <div className={styles.recentWrap}>
      <div className={styles.sectionLabel}>Dieta</div>

      {loading && <div className={styles.noData} style={{ margin: "0 0 16px" }}>Carregando dieta…</div>}

      {!loading && !diet && (
        <div className={styles.noData} style={{ margin: "0 0 16px" }}>Nenhuma dieta cadastrada para este aluno.</div>
      )}

      {!loading && diet && (() => {
        const m = calcDietMacros(diet.meals, diet.weight_kg);
        return (
          <div
            onClick={onOpenDiet}
            title="Clique para abrir a dieta completa"
            style={{
              cursor: "pointer",
              marginBottom: 16,
              borderRadius: 10,
              overflow: "hidden",
              border: "1px solid #2a2a2a",
              transition: "border-color 0.15s",
            }}
            onMouseEnter={(e) => e.currentTarget.style.borderColor = TEAL}
            onMouseLeave={(e) => e.currentTarget.style.borderColor = "var(--surface-secondary)"}
          >
            {/* Header da dieta */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", backgroundColor: "var(--surface-primary)", borderBottom: "1px solid #2a3a2a" }}>
              <div>
                <span style={{ fontSize: 13, fontWeight: 700, color: "var(--text-primary)" }}>{diet.name || `Dieta ${diet.date}`}</span>
                <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)", marginLeft: 8 }}>{diet.date}</span>
                {diet.goal && <span style={{ fontSize: "var(--fs-xs)", color: TEAL, marginLeft: 8 }}>{GOAL_LABELS[diet.goal] || diet.goal}</span>}
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                {diet.target_kcal && (
                  <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)" }}>meta: {diet.target_kcal} kcal</span>
                )}
                <span style={{ fontSize: 12, fontWeight: 700, color: TEAL }}>{m.kcal_total} kcal</span>
                <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)" }}>▶</span>
              </div>
            </div>

            {/* Tabela de macros */}
            <div style={{ overflowX: "auto" }}>
              <table style={{ borderCollapse: "collapse", width: "100%", border: "none" }}>
                <thead>
                  <tr>
                    <th style={{ ...hdr, textAlign: "left" }}>Macros</th>
                    <th style={hdr}>g</th>
                    <th style={hdr}>%</th>
                    <th style={hdr}>Kcal</th>
                    <th style={hdr}>g/kg</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td style={lbl}>Proteína total</td>
                    <td style={bold}>{m.protein_total.g}</td>
                    <td style={bold}>{m.protein_total.pct}%</td>
                    <td style={bold}>{m.protein_total.kcal}</td>
                    <td style={bold}>{m.protein_total.gkg}</td>
                  </tr>
                  <tr>
                    <td style={sub}>Proteína (A)</td>
                    <td style={cell}>{m.protein_animal.g}</td>
                    <td style={cell}>-</td><td style={cell}>-</td><td style={cell}>-</td>
                  </tr>
                  <tr>
                    <td style={sub}>Proteína (V)</td>
                    <td style={cell}>{m.protein_veg.g}</td>
                    <td style={cell}>-</td><td style={cell}>-</td><td style={cell}>-</td>
                  </tr>
                  <tr>
                    <td style={lbl}>Carboidratos</td>
                    <td style={bold}>{m.carbs.g}</td>
                    <td style={bold}>{m.carbs.pct}%</td>
                    <td style={bold}>{m.carbs.kcal}</td>
                    <td style={bold}>{m.carbs.gkg}</td>
                  </tr>
                  <tr>
                    <td style={sub}>Fibras</td>
                    <td style={cell}>{m.fiber.g}</td>
                    <td style={cell}>-</td><td style={cell}>-</td><td style={cell}>-</td>
                  </tr>
                  <tr>
                    <td style={lbl}>Lipídios</td>
                    <td style={bold}>{m.fat.g}</td>
                    <td style={bold}>{m.fat.pct}%</td>
                    <td style={bold}>{m.fat.kcal}</td>
                    <td style={bold}>{m.fat.gkg}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Body Measurements Panel ───────────────────────────────────────────────────
function BodyMeasurementsPanel({ measurements = [], bodyProgressEntries = [] }) {
  const [expanded, setExpanded] = useState(true);

  // Latest body_progress entry as the authoritative current weight
  const latestBP = bodyProgressEntries[0] || null;

  if (!measurements.length && !latestBP) return (
    <div className={styles.recentWrap}>
      <div className={styles.sectionLabel}>Medidas Corporais</div>
      <div className={styles.noData} style={{ margin: "0 0 16px" }}>Aluno ainda não registrou medidas corporais.</div>
    </div>
  );

  const latest = measurements[0] || {}; // sorted desc by date; may be empty if no formal measurements
  const ind = latest.indicators || {};

  // Weight KPI: body_progress takes priority over body_measurements
  const weightValue = latestBP
    ? `${latestBP.weight_kg} kg`
    : (latest.weight_kg != null ? `${latest.weight_kg} kg` : "-");
  const weightUnit  = latestBP
    ? `último reg. ${fmtDate(latestBP.recorded_at)}`
    : (latest.date ? `em ${fmtDate(latest.date)}` : "-");

  // Weight delta: compare latest vs second-to-last body_progress entry
  const prevBP  = bodyProgressEntries[1] || null;
  const weightDelta = latestBP && prevBP
    ? (latestBP.weight_kg - prevBP.weight_kg).toFixed(1)
    : null;
  const deltaLabel = weightDelta != null
    ? ` (${weightDelta > 0 ? "+" : ""}${weightDelta} kg vs anterior)`
    : "";

  const kpis = [
    { label: "Peso atual",    value: weightValue,  unit: weightUnit + deltaLabel, color: AMBER,   tooltip: "Último peso registrado nas Fotos de Progresso" },
    { label: "IMC",           value: ind.bmi != null ? ind.bmi.toFixed(1) : "-",          unit: ind.bmi_category || "",        color: ind.bmi < 18.5 ? BLUE : ind.bmi < 25 ? TEAL : ind.bmi < 30 ? AMBER : "var(--feedback-error)", tooltip: "Índice de Massa Corporal = peso ÷ altura². Normal: 18.5–24.9" },
    { label: "Gordura corp.", value: ind.body_fat_pct != null ? `${ind.body_fat_pct.toFixed(1)}%` : "-", unit: "método U.S. Navy", color: VIOLET, tooltip: "% de gordura corporal estimada pelo método U.S. Navy (circunferências)" },
    { label: "Massa magra",   value: ind.lean_mass_kg != null ? `${ind.lean_mass_kg.toFixed(1)} kg` : "-", unit: "massa muscular estimada", color: TEAL, tooltip: "Peso total menos gordura estimada - indicador de massa muscular" },
    { label: "TMB",           value: ind.bmr_kcal != null ? `${Math.round(ind.bmr_kcal)} kcal` : "-",     unit: "gasto basal/dia", color: BLUE, tooltip: "Taxa Metabólica Basal: calorias gastas em repouso absoluto por dia" },
    { label: "C/Q",           value: ind.waist_hip_ratio != null ? ind.waist_hip_ratio.toFixed(2) : "-",  unit: ind.whr_risk || "-",         color: ind.whr_risk === "Alto" ? "#ff6b6b" : TEAL, tooltip: "Relação Cintura/Quadril - indicador de risco cardiovascular e metabólico" },
  ];

  return (
    <div className={styles.recentWrap}>
      <div className={styles.measHeader}>
        <div className={styles.sectionLabel} style={{ margin: 0 }}>Medidas Corporais</div>
        {latestBP && <span className={styles.measDate}>Peso: {latestBP.weight_kg} kg · {fmtDate(latestBP.recorded_at)}</span>}
        {!latestBP && latest.date && <span className={styles.measDate}>Última: {fmtDate(latest.date)}</span>}
        <button className={styles.measToggle} onClick={() => setExpanded(v => !v)}>
          {expanded ? "Menos ▲" : `Histórico ▼`}
        </button>
      </div>

      <div className={styles.kpiGrid} style={{ margin: "8px 0 0" }}>
        {kpis.map(c => (
          <div key={c.label} className={styles.kpiCard2} data-tooltip={c.tooltip}>
            <div className={styles.kpiCard2Val} style={{ color: c.color, fontSize: 18 }}>{c.value}</div>
            <div className={styles.kpiCard2Label}>{c.label}</div>
            <div className={styles.kpiCard2Unit}>{c.unit}</div>
          </div>
        ))}
      </div>

      {expanded && bodyProgressEntries.length > 0 && (
        <div className={styles.measHistory} style={{ marginBottom: 12 }}>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 700, color: "var(--text-0-50)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Histórico de peso (fotos de progresso)
          </div>
          <table className={styles.measTable}>
            <thead>
              <tr>
                <th>Data</th><th>Peso</th><th>Variação</th>
              </tr>
            </thead>
            <tbody>
              {bodyProgressEntries.slice(0, 10).map((e, i) => {
                const prev = bodyProgressEntries[i + 1];
                const delta = prev ? (e.weight_kg - prev.weight_kg).toFixed(1) : null;
                const deltaColor = delta == null ? "" : delta > 0 ? AMBER : delta < 0 ? TEAL : "inherit";
                return (
                  <tr key={e.id}>
                    <td>{fmtDate(e.recorded_at)}</td>
                    <td style={{ fontWeight: 700 }}>{e.weight_kg} kg</td>
                    <td style={{ color: deltaColor }}>
                      {delta != null ? (delta > 0 ? `+${delta}` : delta) + " kg" : "-"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {expanded && measurements.length > 0 && (
        <div className={styles.measHistory}>
          <div style={{ fontSize: "var(--fs-xs)", fontWeight: 700, color: "var(--text-0-50)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
            Medidas corporais detalhadas
          </div>
          <table className={styles.measTable}>
            <thead>
              <tr>
                <th>Data</th><th>Peso</th><th>IMC</th><th>Gord%</th><th>Cintura</th><th>Quadril</th><th>Braço (D)</th>
              </tr>
            </thead>
            <tbody>
              {measurements.slice(0, 10).map((m, i) => (
                <tr key={i}>
                  <td>{fmtDate(m.date)}</td>
                  <td>{m.weight_kg != null ? `${m.weight_kg} kg` : "-"}</td>
                  <td>{m.indicators?.bmi != null ? m.indicators.bmi.toFixed(1) : "-"}</td>
                  <td>{m.indicators?.body_fat_pct != null ? `${m.indicators.body_fat_pct.toFixed(1)}%` : "-"}</td>
                  <td>{m.waist_cm != null ? `${m.waist_cm} cm` : "-"}</td>
                  <td>{m.hip_cm != null ? `${m.hip_cm} cm` : "-"}</td>
                  <td>{m.arm_cm_right != null ? `${m.arm_cm_right} cm` : "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Student Reports Panel ─────────────────────────────────────────────────────
const CAT_LABELS = {
  treino: [faDumbbell, "Treino"],
  dieta:  [faAppleWhole, "Dieta"],
  outro:  [faCommentDots, "Outro"],
};

function fmtReportDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

function ReportCard({ r, onMarkRead }) {
  const [catIcon, label] = CAT_LABELS[r.category] || [faCommentDots, r.category];
  return (
    <div className={`${styles.reportCard} ${!r.read_at ? styles.reportCardUnread : ""}`}>
      <div className={styles.reportCardTop}>
        <span className={styles.reportCatBadge}><FontAwesomeIcon icon={catIcon} /> {label}</span>
        <span className={styles.reportDate}>{fmtReportDate(r.created_at)}</span>
      </div>
      {r.tags?.length > 0 && (
        <div className={styles.reportTags}>
          {r.tags.map(t => <span key={t} className={styles.reportTag}>{t}</span>)}
        </div>
      )}
      <div className={styles.reportDesc}>{r.description}</div>
      {!r.read_at && (
        <button className={styles.reportMarkRead} onClick={() => onMarkRead(r.id)}>
          <FontAwesomeIcon icon={faCheck} /> Marcar como lido
        </button>
      )}
    </div>
  );
}

function ReportsHistoryModal({ reports, onMarkRead, onClose }) {
  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: "rgba(0,0,0,0.70)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 16,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--surface-secondary)",
          border: "1px solid var(--border-0-10)",
          borderRadius: 18,
          width: "100%", maxWidth: 520,
          maxHeight: "82vh",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
          boxShadow: "0 28px 70px var(--border-0-65)",
        }}
      >
        <ModalHeader onClose={onClose} />
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", padding: "16px 20px 12px", borderBottom: "1px solid var(--border-0-06)" }}>
          <span style={{ fontWeight: 800, fontSize: 16, color: "var(--text-primary)" }}>
            <FontAwesomeIcon icon={faFlag} style={{ marginRight: 8, color: "var(--feedback-success)" }} />
            Histórico de relatos
          </span>
        </div>

        {/* List */}
        <div style={{ overflowY: "auto", padding: "12px 16px", flex: 1 }}>
          {reports.length === 0 && (
            <div style={{ textAlign: "center", color: "var(--text-0-40)", fontSize: 14, paddingTop: 32 }}>Nenhum relato registrado.</div>
          )}
          {reports.map(r => (
            <ReportCard key={r.id} r={r} onMarkRead={onMarkRead} />
          ))}
        </div>
      </div>
    </div>,
    document.body
  );
}

function StudentReportsPanel({ studentId, onReportRead = null }) {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    if (!studentId) return;
    let cancelled = false;
    setLoading(true);
    api.get(`/student-reports?student_id=${studentId}`)
      .then(r => { if (!cancelled) setReports(r.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [studentId]);

  async function markRead(reportId) {
    await api.patch(`/student-reports/${reportId}/read`).catch(() => {});
    setReports(prev => {
      const updated = prev.map(r => r.id === reportId ? { ...r, read_at: new Date().toISOString() } : r);
      // Notifica o dashboard se não houver mais não lidos
      const stillUnread = updated.some(r => !r.read_at);
      if (!stillUnread && onReportRead) onReportRead(studentId);
      return updated;
    });
  }

  const unread = reports.filter(r => !r.read_at);
  const total = reports.length;

  return (
    <div className={styles.reportsWrap}>
      <div className={styles.reportsHeader}>
        <span className={styles.reportsTitle}>
          <FontAwesomeIcon icon={faFlag} style={{ marginRight: 5 }} />
          Relatos do aluno
          {unread.length > 0 && <span className={styles.reportsBadge}>{unread.length} novo{unread.length !== 1 ? "s" : ""}</span>}
        </span>
        {total > 0 && (
          <button className={styles.reportsHistoryBtn} onClick={() => setShowHistory(true)}>
            Ver todos ({total})
          </button>
        )}
      </div>

      {loading && <div className={styles.reportsEmpty}>Carregando…</div>}

      {!loading && unread.length === 0 && (
        <div className={styles.reportsEmpty}>
          {total === 0 ? "Nenhum relato enviado." : "Nenhum relato pendente de leitura."}
        </div>
      )}

      {unread.map(r => (
        <ReportCard key={r.id} r={r} onMarkRead={markRead} />
      ))}

      {showHistory && (
        <ReportsHistoryModal
          reports={reports}
          onMarkRead={markRead}
          onClose={() => setShowHistory(false)}
        />
      )}
    </div>
  );
}

// ── Student Locations Map ───────────────────────────────────────────────────
// Ícone de pino self-contained (estilo inline, não depende de CSS module externo)
const studentPinIcon = L.divIcon({
  className: "",
  html: '<span style="display:block;width:18px;height:18px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:#1d9e75;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.35);"></span>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});

function FitBoundsToMarkers({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (!positions.length) return;
    // Sem animação: o enquadramento animado deixava uma transição de zoom em
    // andamento, e se o mapa saísse da tela antes de ela terminar (fechar o
    // painel, trocar de aluno) o Leaflet quebrava no fim da animação
    // ("can't access property _leaflet_pos, el is undefined").
    if (positions.length === 1) {
      map.setView(positions[0], 15, { animate: false });
    } else {
      map.fitBounds(positions, { padding: [30, 30], maxZoom: 15, animate: false });
    }
    const id = setTimeout(() => map.invalidateSize(), 100);
    // Sem map.stop() na saída: quando este efeito limpa, o MapContainer já
    // removeu o mapa, e stop() quebrava do mesmo jeito. Sem animação, não
    // há movimento em andamento pra interromper.
    return () => clearTimeout(id);
  }, [map, positions]);
  return null;
}

function fmtLocationDate(iso) {
  if (!iso) return "";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    dateStyle: "short",
    timeStyle: "short",
  }).format(new Date(iso));
}

function StudentLocationsMap({ locations = [] }) {
  const positions = useMemo(
    () => locations.map((l) => [l.lat, l.lon]),
    [locations]
  );

  if (!locations.length) {
    return (
      <p style={{ fontSize: 13, color: "var(--text-secondary)", textAlign: "center", marginTop: 16 }}>
        Nenhum check-in com localização registrada no período.
      </p>
    );
  }

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ height: 320, borderRadius: 14, overflow: "hidden", border: "1px solid var(--border-0-08)" }}>
        <MapContainer center={positions[0]} zoom={13} scrollWheelZoom={true} style={{ width: "100%", height: "100%" }}>
          <FitBoundsToMarkers positions={positions} />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {locations.map((loc) => (
            <Marker key={loc.id} position={[loc.lat, loc.lon]} icon={studentPinIcon}>
              <Popup>
                <strong>{loc.title}</strong>
                <br />
                {loc.label || "Local não identificado"}
                <br />
                {fmtLocationDate(loc.date)}
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6, maxHeight: 160, overflowY: "auto" }}>
        {locations.map((loc) => (
          <div
            key={loc.id}
            style={{
              display: "flex", alignItems: "center", gap: 8,
              fontSize: 12, color: "var(--text-secondary)",
              padding: "6px 8px", borderRadius: 8, background: "var(--border-0-06)",
            }}
          >
            <FontAwesomeIcon icon={faLocationDot} style={{ color: TEAL, flexShrink: 0 }} />
            <span style={{ color: "var(--text-primary)", fontWeight: 600, flexShrink: 0 }}>
              {loc.label || "Local não identificado"}
            </span>
            <span style={{ marginLeft: "auto", flexShrink: 0 }}>{fmtLocationDate(loc.date)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Recent Sessions ───────────────────────────────────────────────────────────
// onSessionClick: (sessionId | null, checkin) => void
// onDeleteCheckin: (checkinId, studentId) => Promise<void>
const PAGE_SIZE = 5;

function RecentSessions({ recent = [], onSessionClick, onDeleteCheckin, studentId }) {
  const [openFeedback, setOpenFeedback] = useState(null);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [sent, setSent] = useState({});
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [page, setPage] = useState(0);

  if (!recent.length) return null;

  const totalPages = Math.ceil(recent.length / PAGE_SIZE);
  const paginated = recent.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  async function submitFeedback(userExerciseId) {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post("/session-feedback", { user_exercise_id: userExerciseId, comment: text.trim() });
      setSent(prev => ({ ...prev, [userExerciseId]: text.trim() }));
      setText("");
      setOpenFeedback(null);
    } catch (e) {
      console.error("Feedback error:", e);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className={styles.recentWrap}>
      <div className={styles.sectionLabel}>Últimas sessões</div>
      <div className={styles.recentList}>
        {paginated.map((r, i) => {
          const hasFeedback = !!sent[r.id] || (r.feedback_count > 0);
          const isOpen = openFeedback === r.id;
          return (
            <div key={i} className={styles.recentEntry}>
              {(r.plan_title || r.exercise_name || r.is_cardio) && (
                <div className={styles.recentPlanTitle}>
                  {r.is_cardio && (
                    <FontAwesomeIcon icon={faPersonRunning} className={styles.cardioPlanIcon} />
                  )}
                  {r.plan_title || r.exercise_name || "Cardio"}
                </div>
              )}
              <div className={styles.recentRow}>
                <span className={styles.recentDate}>{fmtDate(r.date)}</span>
                <span className={styles.recentDur}>{fmtMin(r.duration)}</span>
                {r.weight && <span className={styles.recentWeight}>{r.weight} kg</span>}
                <span className={styles.recentSpacer} />
                <span className={styles.recentPts}>+{r.points} pts</span>
                <div className={styles.recentActions}>
                {onSessionClick && (
                  <button
                    className={styles.detailBtn}
                    onClick={() => onSessionClick(r.workout_session_id || null, r)}
                  >
                    <FontAwesomeIcon icon={faClipboardList} /> Ver detalhe
                  </button>
                )}
                {r.total_volume > 0 && (
                  <span className={styles.volumeBadge} title="Volume Total = séries × reps × peso">
                    <FontAwesomeIcon icon={faCheck} style={{ marginRight: 4 }} />{r.total_volume.toLocaleString("pt-BR")} kg
                  </span>
                )}
                {r.id && (
                  <button
                    className={`${styles.feedbackBtn} ${hasFeedback ? styles.feedbackBtnSent : ""}`}
                    onClick={() => { setOpenFeedback(isOpen ? null : r.id); setText(""); }}
                  >
                    <FontAwesomeIcon icon={faCommentDots} /> {hasFeedback ? `Feedback (${(r.feedback_count || 0) + (sent[r.id] ? 1 : 0)})` : "Dar feedback"}
                  </button>
                )}
                </div>
                {onDeleteCheckin && r.id && (
                  confirmDeleteId === r.id ? (
                    <div style={{ display:'flex', alignItems:'center', gap:4 }}>
                      <span style={{ fontSize:'var(--fs-xs)', color:'var(--text-0-50)' }}>Excluir?</span>
                      <button
                        className={`${styles.deleteCheckinBtn} touchTarget`}
                        style={{ opacity:1, background:'rgba(239,68,68,0.18)', border:'1px solid rgba(239,68,68,0.4)', color:'#fca5a5', padding:'6px 12px', fontSize:'var(--fs-xs)', fontWeight:700 }}
                        disabled={deletingId === r.id}
                        onClick={async () => {
                          setDeletingId(r.id);
                          setConfirmDeleteId(null);
                          try { await onDeleteCheckin(r.id, studentId); }
                          catch(err) { console.error(err); }
                          finally { setDeletingId(null); }
                        }}
                      >
                        {deletingId === r.id ? '…' : 'Sim'}
                      </button>
                      <button
                        className={`${styles.deleteCheckinBtn} touchTarget`}
                        style={{ opacity:0.8, fontSize:'var(--fs-xs)', padding:'6px 12px' }}
                        onClick={() => setConfirmDeleteId(null)}
                      >
                        Não
                      </button>
                    </div>
                  ) : (
                    <button
                      className={`${styles.deleteCheckinBtn} touchTarget`}
                      title="Excluir check-in"
                      aria-label="Excluir check-in"
                      disabled={deletingId === r.id}
                      onClick={() => setConfirmDeleteId(r.id)}
                    >
                      <FontAwesomeIcon icon={faTrashCan} />
                    </button>
                  )
                )}
              </div>
              {isOpen && (
                <div className={styles.feedbackBox}>
                  {sent[r.id] ? (
                    <div className={styles.feedbackSent}><FontAwesomeIcon icon={faCheck} /> Feedback enviado: "{sent[r.id]}"</div>
                  ) : (
                    <>
                      <textarea
                        className={styles.feedbackTextarea}
                        placeholder="Escreva seu feedback para esta sessão… (ex: ótima progressão no agachamento, tente aumentar o peso na próxima)"
                        value={text}
                        onChange={e => setText(e.target.value)}
                        rows={3}
                        autoFocus
                      />
                      <div className={styles.feedbackActions}>
                        <button className={styles.feedbackCancel} onClick={() => setOpenFeedback(null)}>Cancelar</button>
                        <button
                          className={styles.feedbackSend}
                          disabled={!text.trim() || saving}
                          onClick={() => submitFeedback(r.id)}
                        >
                          {saving ? "Enviando…" : "Enviar feedback"}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      {totalPages > 1 && (
        <div className={styles.paginationRow}>
          <button
            className={styles.pageBtn}
            disabled={page === 0}
            onClick={() => setPage(p => p - 1)}
          >‹ Anterior</button>
          <span className={styles.pageInfo}>{page + 1} / {totalPages}</span>
          <button
            className={styles.pageBtn}
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >Próxima ›</button>
        </div>
      )}
    </div>
  );
}

// ── Insight History Row ───────────────────────────────────────────────────────
function InsightHistoryRow({ item, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const dateLabel = item.generated_at
    ? new Date(item.generated_at).toLocaleDateString("pt-BR", { day: "2-digit", month: "short", year: "numeric" })
    : "";
  const lines = (item.insights_text || "").split("\n").filter(l => l.trim());
  const preview = lines[0] || "";

  return (
    <div style={{
      borderBottom: "1px solid var(--border-0-06)",
      padding: "10px 16px",
    }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "flex-start", justifyContent: "space-between",
          gap: 8, padding: 0, textAlign: "left",
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ color: "var(--feedback-warning)", fontSize: "var(--fs-xs)", fontWeight: 600, marginBottom: 3 }}>
            {dateLabel} · {item.weeks_analyzed}sem analisadas
          </div>
          {!open && (
            <div style={{
              color: "var(--text-0-60)", fontSize: 12,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}>
              {preview}
            </div>
          )}
        </div>
        <FontAwesomeIcon
          icon={open ? faChevronUp : faChevronDown}
          style={{ color: "var(--text-0-40)", fontSize: "var(--fs-xs)", marginTop: 2, flexShrink: 0 }}
        />
      </button>
      {open && (
        <div style={{ marginTop: 8 }}>
          {lines.map((line, i) => (
            <div key={i} style={{
              color: "var(--text-0-70)", fontSize: 12,
              lineHeight: 1.55, paddingBottom: 4,
            }}>
              {line}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Collapsible Section ──────────────────────────────────────────────────────
function CollapsibleSection({ title, icon, defaultOpen = true, badge, children }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ marginBottom: 6 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "rgba(var(--text-primary-rgb),0.03)", border: "none", borderTop: "1px solid var(--border-0-08)",
          padding: "10px 24px", cursor: "pointer", gap: 8,
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 7, fontSize: "var(--fs-xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "var(--text-0-50)" }}>
          {icon && <span style={{ fontSize: 12 }}>{icon}</span>}
          {title}
          {badge != null && <span style={{ background: "rgba(var(--accent-rgb),0.18)", color: "var(--accent)", border: "1px solid rgba(var(--accent-rgb),0.30)", borderRadius: 999, padding: "1px 7px", fontSize: "var(--fs-xs)", fontWeight: 700 }}>{badge}</span>}
        </span>
        <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} style={{ color: "var(--text-0-40)", fontSize: "var(--fs-xs)" }} />
      </button>
      {open && <div>{children}</div>}
    </div>
  );
}

// ── Time Chips ────────────────────────────────────────────────────────────────
const TIME_BUCKETS = [
  { label: "Madrugada", range: "0h–5h",  hours: [0,1,2,3,4,5],   color: "#7c3aed", bg: "rgba(124,58,237,0.12)" },
  { label: "Manhã",     range: "6h–11h", hours: [6,7,8,9,10,11], color: "#f59e0b", bg: "rgba(245,158,11,0.12)" },
  { label: "Tarde",     range: "12h–17h",hours: [12,13,14,15,16,17], color: "#3b82f6", bg: "rgba(59,130,246,0.12)" },
  { label: "Noite",     range: "18h–23h",hours: [18,19,20,21,22,23], color: "#10b981", bg: "rgba(16,185,129,0.12)" },
];

function TimeChips({ byHour = [] }) {
  const counts = {};
  byHour.forEach(({ hour, count }) => { counts[hour] = count; });

  const buckets = TIME_BUCKETS.map(b => {
    const total = b.hours.reduce((s, h) => s + (counts[h] || 0), 0);
    // peak hour within bucket
    let peakHour = null, peakCount = 0;
    b.hours.forEach(h => { if ((counts[h] || 0) > peakCount) { peakCount = counts[h]; peakHour = h; } });
    return { ...b, total, peakHour };
  }).filter(b => b.total > 0);

  const maxTotal = Math.max(1, ...buckets.map(b => b.total));

  if (!buckets.length) return (
    <div style={{ padding: "10px 0 4px", color: "var(--text-0-40)", fontSize: 13, textAlign: "center" }}>
      Sem dados de horário no período
    </div>
  );

  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10, padding: "6px 0 4px" }}>
      {buckets.map(b => (
        <div key={b.label} style={{
          display: "flex", alignItems: "center", gap: 10,
          background: b.bg, border: `1px solid ${b.color}44`,
          borderRadius: 12, padding: "10px 16px", flex: "1 1 140px",
          position: "relative", overflow: "hidden",
        }}>
          {/* bar de fundo proporcional */}
          <div style={{
            position: "absolute", left: 0, top: 0, bottom: 0,
            width: `${Math.round((b.total / maxTotal) * 100)}%`,
            background: b.color + "1A", borderRadius: 12,
            pointerEvents: "none",
          }} />
          <div style={{ position: "relative" }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: b.color, lineHeight: 1 }}>{b.total}</div>
            <div style={{ fontSize: "var(--fs-xs)", fontWeight: 700, color: "var(--text-0-50)", textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 2 }}>treinos</div>
          </div>
          <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1 }}>{b.label}</div>
            <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-0-40)", marginTop: 2 }}>
              {b.peakHour != null ? `Pico: ${b.peakHour}h · ${b.range}` : b.range}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Weight Sparkline ──────────────────────────────────────────────────────────
function WeightSparkline({ entries = [] }) {
  if (entries.length < 2) return null;
  const pts = [...entries].reverse(); // ascending order
  const vals = pts.map(e => e.weight_kg);
  const minV = Math.min(...vals), maxV = Math.max(...vals);
  const range = maxV - minV || 1;
  const W = 160, H = 40, PAD = 4;

  const x = (i) => PAD + (i / (pts.length - 1)) * (W - PAD * 2);
  const y = (v) => H - PAD - ((v - minV) / range) * (H - PAD * 2);

  const d = pts.map((e, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(e.weight_kg).toFixed(1)}`).join(" ");
  const first = pts[0].weight_kg, last = pts[pts.length - 1].weight_kg;
  const delta = last - first;
  const color = delta < 0 ? "#34d399" : delta > 0 ? "#fb923c" : "#94a3b8";

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 16px", background: "rgba(245,158,11,0.06)", border: "1px solid rgba(245,158,11,0.15)", borderRadius: 12, marginTop: 10 }}>
      <div>
        <div style={{ fontSize: 18, fontWeight: 800, color: AMBER, lineHeight: 1 }}>{last} kg</div>
        <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-0-40)", marginTop: 2 }}>
          {delta !== 0 ? (delta > 0 ? `+${delta.toFixed(1)}` : delta.toFixed(1)) + " kg no período" : "Estável"}
        </div>
      </div>
      <svg width={W} height={H} style={{ flex: 1, minWidth: 60, maxWidth: W }}>
        <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
        {pts.map((e, i) => (
          <circle key={i} cx={x(i)} cy={y(e.weight_kg)} r={i === pts.length - 1 ? 3.5 : 2} fill={color} opacity={i === pts.length - 1 ? 1 : 0.5} />
        ))}
      </svg>
      <div style={{ fontSize: "var(--fs-xs)", color: color, fontWeight: 700, flexShrink: 0 }}>
        {delta > 0 ? "▲ +" : delta < 0 ? "▼ " : "- "}{delta !== 0 ? Math.abs(delta).toFixed(1) + " kg" : "estável"}
      </div>
    </div>
  );
}

// ── Load Progression Section ──────────────────────────────────────────────────
function LoadProgressionSection({ data = [] }) {
  if (!data.length) return (
    <div className="chartCard" style={{ margin: "0 24px 14px", background: "rgba(var(--surface-primary-rgb), 0.70)", border: "1px solid var(--border-0-06)", borderRadius: 16, padding: "14px 16px" }}>
      <div style={{ fontSize: "var(--fs-xs)", fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-0-50)", marginBottom: 8, textTransform: "uppercase" }}>Top 6 mais executados · Progressão de carga</div>
      <div style={{ color: "var(--text-0-40)", fontSize: 13, textAlign: "center", padding: "16px 0" }}>Sem dados de carga no período</div>
    </div>
  );

  return (
    <div style={{ margin: "0 24px 14px", background: "rgba(var(--surface-primary-rgb), 0.70)", border: "1px solid var(--border-0-06)", borderRadius: 16, padding: "14px 16px" }}>
      <div style={{ fontSize: "var(--fs-xs)", fontWeight: 700, letterSpacing: "0.06em", color: "var(--text-0-50)", marginBottom: 12, textTransform: "uppercase" }}>Top 6 mais executados · Progressão de carga</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12 }}>
        {data.map(({ exercise, series }) => {
          if (!series.length) return null;
          const vals = series.map(s => s.max_weight);
          const minV = Math.min(...vals), maxV = Math.max(...vals);
          const range = maxV - minV || 1;
          const W = 160, H = 48, PAD = 4;
          const x = (i) => PAD + (i / Math.max(1, series.length - 1)) * (W - PAD * 2);
          const y = (v) => H - PAD - ((v - minV) / range) * (H - PAD * 2);
          const d = series.map((s, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(s.max_weight).toFixed(1)}`).join(" ");
          const first = vals[0], last = vals[vals.length - 1];
          const delta = last - first;
          const color = delta > 0 ? "#34d399" : delta < 0 ? "#fb923c" : "#94a3b8";

          return (
            <div key={exercise} style={{ background: "var(--border-0-04)", border: "1px solid var(--border-0-08)", borderRadius: 10, padding: "10px 12px" }}>
              <div style={{ fontSize: "var(--fs-xs)", fontWeight: 700, color: "var(--text-0-70)", marginBottom: 6, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={exercise}>{exercise}</div>
              <div style={{ display: "flex", alignItems: "flex-end", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 800, color, lineHeight: 1 }}>{last} kg</div>
                  <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-0-40)", marginTop: 2 }}>
                    {delta !== 0 ? (delta > 0 ? "+" : "") + delta.toFixed(1) + " kg" : "estável"}
                  </div>
                </div>
                <svg width={W} height={H} style={{ flex: 1, minWidth: 60 }}>
                  <defs>
                    <linearGradient id={`glp-${exercise.replace(/\s/g,"")}`} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={color} stopOpacity="0.25" />
                      <stop offset="100%" stopColor={color} stopOpacity="0.02" />
                    </linearGradient>
                  </defs>
                  {series.length > 1 && (
                    <path
                      d={d + ` L${x(series.length-1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`}
                      fill={`url(#glp-${exercise.replace(/\s/g,"")})`}
                    />
                  )}
                  <path d={d} fill="none" stroke={color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                  {series.map((s, i) => (
                    <circle key={i} cx={x(i)} cy={y(s.max_weight)} r={i === series.length-1 ? 3 : 2} fill={color} opacity={i === series.length-1 ? 1 : 0.5} />
                  ))}
                </svg>
              </div>
              {series.length > 1 && (
                <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-0-40)", marginTop: 4 }}>
                  {(w => { const p = w.split('-'); return `${p[2]}/${p[1]}`; })(series[0].week)} → {(w => { const p = w.split('-'); return `${p[2]}/${p[1]}`; })(series[series.length-1].week)} · {series.length} sem
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function TeacherAnalyticsModal({ open, onClose, inline = false, apiUrl = "", initialStudentId = null, onReportRead = null }) {
  useBodyScrollLock();
  // Voltar do Android fecha o painel — mas so no modo modal: `inline` e uma
  // secao da pagina, e sequestrar o voltar ali levaria o usuario pra fora do
  // dashboard sem nada visivel tendo fechado.
  useHistoryBackClose(open && !inline, onClose);
  const [error, setError]         = useState("");
  const [primaryId, setPrimaryId] = useState(null);

  // Pré-selecionar aluno vindo do TeacherDashboard — único jeito de abrir este
  // modal hoje; ele sempre mostra os dados de um único aluno por vez.
  useEffect(() => {
    if (initialStudentId) setPrimaryId(initialStudentId);
  }, [initialStudentId]);

  const MAX_DAYS = 180;
  const [days, setDays]               = useState(30);
  const [customInput, setCustomInput] = useState("30");
  const [showCustomDate, setShowCustomDate] = useState(false);
  const toDate   = useMemo(() => dateToLocalISO(new Date()), []);
  const fromDate = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() - days); return dateToLocalISO(d);
  }, [days]);
  const [profiles, setProfiles]       = useState({});
  const [series, setSeries]           = useState({});
  const [measurements, setMeasurements] = useState({});  // { studentId: [...] }
  const [bodyProgress, setBodyProgress] = useState({});  // { studentId: [...] } sorted desc
  const [sessionDetailId, setSessionDetailId] = useState(null);
  const [openNutrition, setOpenNutrition] = useState(false);
  const [showStudentPhotos, setShowStudentPhotos] = useState(false);

  // AI Insights — persistido no DB
  // { [studentId]: { text, generatedAt, nextAvailableAt, weeks } }
  const [aiInsights, setAiInsights]         = useState({});
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [insightsWeeks, setInsightsWeeks]     = useState(6);
  const [showWorkoutHistory, setShowWorkoutHistory] = useState(false);
  const [showSendPush, setShowSendPush] = useState(false);
  const [insightExpanded, setInsightExpanded] = useState(false);
  const [showInsightHistory, setShowInsightHistory] = useState(false);
  const [insightHistory, setInsightHistory]   = useState([]);
  const [insightHistoryLoading, setInsightHistoryLoading] = useState(false);
  const [showInsightConfirm, setShowInsightConfirm]     = useState(false);

  // Carrega último insight do DB quando muda de aluno
  useEffect(() => {
    if (!primaryId) return;
    if (aiInsights[primaryId]) return;   // já carregado nessa sessão
    api.get(`/ai/student-insights/${primaryId}`)
      .then(res => {
        if (res.data) {
          setAiInsights(prev => ({
            ...prev,
            [primaryId]: {
              text: res.data.insights,
              generatedAt: res.data.generated_at,
              nextAvailableAt: res.data.next_available_at,
              weeks: res.data.period_weeks,
            },
          }));
        }
      })
      .catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryId]);

  const getInsightsMeta = useCallback((studentId) => {
    const entry = aiInsights[studentId];
    if (!entry?.nextAvailableAt) return { canGenerate: true, lastTs: null, nextTs: null };
    const nextTs = new Date(entry.nextAvailableAt);
    const canGenerate = Date.now() >= nextTs.getTime();
    return { canGenerate, lastTs: entry.generatedAt ? new Date(entry.generatedAt) : null, nextTs };
  }, [aiInsights]);

  const handleGenerateInsights = useCallback(async (studentId) => {
    setInsightsLoading(true);
    try {
      const res = await api.post("/ai/student-insights", { student_id: studentId, weeks: insightsWeeks });
      const data = res.data;
      setAiInsights(prev => ({
        ...prev,
        [studentId]: {
          text: data.insights,
          generatedAt: data.generated_at || new Date().toISOString(),
          nextAvailableAt: data.next_available_at,
          weeks: insightsWeeks,
        },
      }));
      setInsightExpanded(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 429 && detail?.next_available_at) {
        setAiInsights(prev => ({
          ...prev,
          [studentId]: { ...(prev[studentId] || {}), nextAvailableAt: detail.next_available_at },
        }));
      }
    } finally {
      setInsightsLoading(false);
    }
  }, [insightsWeeks]);

  const loadStudentData = useCallback(async (id) => {
    if (!id) return;
    setError("");
    try {
      const [profRes, cRes, measRes, bpRes] = await Promise.all([
        api.get(`/dashboard/professor/student/${id}/profile`, { params: { from: fromDate, to: toDate } }),
        api.get("/dashboard/professor/timeseries", { params: { metric: "cadence",  from: fromDate, to: toDate, student_ids: String(id), scope: "org" } }),
        api.get(`/body-measurements/student/${id}`).catch(() => ({ data: [] })),
        api.get(`/body-progress/student/${id}`).catch(() => ({ data: [] })),
      ]);
      setProfiles((p) => ({ ...p, [id]: profRes.data }));
      setSeries((s) => ({ ...s, [id]: { cadence: cRes.data?.series?.[0]?.points || [] } }));
      setMeasurements((m) => ({ ...m, [id]: Array.isArray(measRes.data) ? measRes.data : [] }));
      setBodyProgress((bp) => ({ ...bp, [id]: Array.isArray(bpRes.data) ? bpRes.data : [] }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Erro ao carregar dados");
    } finally { }
  }, [fromDate, toDate]);

  useEffect(() => { if (open && primaryId) loadStudentData(primaryId); }, [open, primaryId, loadStudentData]);
  useEffect(() => { setInsightExpanded(false); setInsightHistory([]); setShowInsightHistory(false); }, [primaryId]);

  const profileA      = primaryId ? profiles[primaryId]     : null;
  const seriesA       = primaryId ? series[primaryId]       : null;
  const measurementsA  = useMemo(() => primaryId ? (measurements[primaryId]  || []) : [], [primaryId, measurements]);
  const bodyProgressA  = useMemo(() => primaryId ? (bodyProgress[primaryId]  || []) : [], [primaryId, bodyProgress]);

  const cadenceChart = useMemo(() => {
    const pts = seriesA?.cadence || [];
    return { labels: pts.map((p) => p.x.replace(/-W/, " S")), datasets: [{ label: "Treinos/semana", data: pts.map((p) => p.y), borderColor: TEAL, backgroundColor: TEAL + "22", tension: 0.3, fill: true }] };
  }, [seriesA]);

  const volumeChart = useMemo(() => {
    const pts = profileA?.volume_weekly || [];
    if (!pts.length) return { labels: [], datasets: [] };
    return {
      labels: pts.map((p) => p.week.replace(/-W/, " S")),
      datasets: [{
        label: "Carga (kg)",
        data: pts.map((p) => p.volume),
        borderColor: AMBER,
        backgroundColor: AMBER + "22",
        tension: 0.3,
        fill: true,
        pointRadius: 4,
        pointBackgroundColor: AMBER,
      }],
    };
  }, [profileA]);

  const weekdayChart = useMemo(() => {
    const bwd = profileA?.by_weekday || [];
    return { labels: WEEKDAY_LABELS, datasets: [{ label: "Treinos", data: bwd.map((r) => r.count), backgroundColor: TEAL + "99" }] };
  }, [profileA]);

  const cardioChart = useMemo(() => {
    const pts = profileA?.cardio_weekly || [];
    if (!pts.length) return { labels: [], datasets: [] };
    return {
      labels: pts.map(p => p.week.replace(/-W/, " S")),
      datasets: [{
        label: "Minutos",
        data: pts.map(p => p.total_min),
        backgroundColor: TEAL + "bb",
        borderRadius: 4,
      }],
    };
  }, [profileA]);

  if (!open && !inline) return null;

  const content = (
    <div
      className={inline ? styles.panel : styles.modal}
      /* Em modo `inline` isto e uma secao embutida na pagina, nao um dialogo —
         marcar como dialog ali seria mentira pro leitor de tela. */
      role={inline ? undefined : "dialog"}
      aria-modal={inline ? undefined : "true"}
      aria-labelledby={inline ? undefined : "tam-title"}
      onClick={inline ? undefined : (e) => e.stopPropagation()}
    >
      {!inline && <ModalHeader onClose={onClose} />}
      <div className={styles.headerArea}>
        <div className={styles.header}>
          <h2 id="tam-title" className={styles.headerTitle}>Acompanhamento</h2>
        </div>
        <div className={styles.controls}>
          <div className={styles.dateRow}>
            {[{ label: "7 dias", value: 7 }, { label: "30 dias", value: 30 }, { label: "Custom", value: "custom" }].map((btn) => (
              <button
                key={btn.value}
                className={`${styles.periodBtn} ${(btn.value === "custom" ? showCustomDate : days === btn.value && !showCustomDate) ? styles.periodBtnActive : ""}`}
                onClick={() => {
                  if (btn.value === "custom") { setShowCustomDate(!showCustomDate); }
                  else { setDays(btn.value); setCustomInput(String(btn.value)); setShowCustomDate(false); }
                }}
              >{btn.label}</button>
            ))}
            {showCustomDate && (
              <input
                type="number" min="1" max={MAX_DAYS}
                value={customInput}
                className={styles.customDaysInput}
                onChange={(e) => {
                  const v = e.target.value;
                  setCustomInput(v);
                  if (!v || v === "0") return;
                  const n = parseInt(v);
                  if (!isNaN(n) && n >= 1 && n <= MAX_DAYS) setDays(n);
                }}
              />
            )}
            {showCustomDate && <span className={styles.dateLabel}>máx {MAX_DAYS} dias</span>}
          </div>
        </div>
      </div>

      {/* ── Detail layout — sempre um único aluno, vindo de initialStudentId ── */}
      <div className={styles.sidebarLayout}>
        <div className={styles.detailPane}>
          {/* Detail content */}
          <div className={styles.detailContent}>
            {error && <div className={styles.errorBox}>{error}</div>}
            {!primaryId && <div className={styles.emptyState}>Carregando dados do aluno…</div>}
            {primaryId && profileA && (
        <div className={styles.individualWrap}>
          <ProfileBanner profile={profileA} onSendPush={() => setShowSendPush(true)} />

          {/* ── Card de Insights IA ── */}
          {(() => {
            const { canGenerate, lastTs, nextTs } = getInsightsMeta(primaryId);
            const entry = aiInsights[primaryId];
            const fmtDt = (d) => d ? d.toLocaleDateString("pt-BR", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit" }) : "";
            return (
              <div className={styles.chartCard} style={{ marginBottom: 12 }}>

                {/* Cabeçalho */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 10, marginBottom: entry ? 14 : 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <FontAwesomeIcon icon={faLightbulb} style={{ color: "var(--feedback-warning)", fontSize: 15 }} />
                    <span className={styles.chartTitle} style={{ margin: 0 }}>Insights IA</span>
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                    {/* Seletor de intervalo */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ fontSize: 12, color: "var(--text-0-50)" }}>Analisar</span>
                      <select
                        value={insightsWeeks}
                        onChange={e => setInsightsWeeks(Number(e.target.value))}
                        style={{
                          background: "var(--surface-secondary)", border: "1px solid var(--border-0-15)",
                          borderRadius: 7, padding: "4px 8px", color: "var(--text-primary)",
                          fontSize: 12, colorScheme: "dark", outline: "none",
                        }}
                      >
                        {[2,4,6,8,12].map(w => <option key={w} value={w}>{w} semanas</option>)}
                      </select>
                    </div>

                    {/* Botão gerar */}
                    <button
                      onClick={() => setShowInsightConfirm(true)}
                      disabled={insightsLoading || !canGenerate}
                      title={!canGenerate ? `Próxima geração disponível em ${fmtDt(nextTs)}` : ""}
                      style={{
                        display: "flex", alignItems: "center", gap: 7,
                        padding: "6px 14px", borderRadius: 10, border: "none",
                        background: (!canGenerate || insightsLoading)
                          ? "rgba(var(--accent-rgb),0.18)"
                          : "var(--accent)",
                        color: (!canGenerate || insightsLoading) ? "rgba(var(--accent-rgb),0.5)" : "var(--on-accent)",
                        fontWeight: 700, fontSize: 13,
                        cursor: (!canGenerate || insightsLoading) ? "not-allowed" : "pointer",
                        boxShadow: (canGenerate && !insightsLoading) ? "0 3px 12px rgba(var(--accent-rgb),0.3)" : "none",
                        transition: "all .15s",
                      }}
                    >
                      {insightsLoading
                        ? <><FontAwesomeIcon icon={faSpinner} spin /> Analisando...</>
                        : <><FontAwesomeIcon icon={faWandMagicSparkles} /> {entry ? "Regenerar" : "Gerar insights"}</>
                      }
                    </button>
                  </div>
                </div>

                {/* Info de limite semanal */}
                <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-0-40)", marginBottom: entry ? 10 : 0, lineHeight: 1.5 }}>
                  {lastTs && canGenerate && `Última geração: ${fmtDt(lastTs)}`}
                  {lastTs && !canGenerate && (
                    <span style={{ color: "var(--feedback-warning)" }}>
                      ⏳ Limite semanal atingido - próxima geração disponível em {fmtDt(nextTs)}
                    </span>
                  )}
                  {!lastTs && "1 geração por aluno por semana."}
                </div>



                {/* Texto dos insights */}
                {!insightsLoading && entry?.text && (() => {
                  const lines = entry.text.split("\n").filter(l => l.trim());
                  const openHistory = async () => {
                    setShowInsightHistory(true);
                    if (insightHistory.length === 0) {
                      setInsightHistoryLoading(true);
                      try {
                        const res = await api.get(`/ai/student-insights/${primaryId}/history`);
                        setInsightHistory(res.data || []);
                      } catch { setInsightHistory([]); }
                      finally { setInsightHistoryLoading(false); }
                    }
                  };
                  return (
                    <>
                      {/* Conteúdo — visível só quando expandido */}
                      {insightExpanded && (
                        <div style={{
                          background: "rgba(251,191,36,0.06)",
                          border: "1px solid rgba(251,191,36,0.18)",
                          borderRadius: 10, padding: "14px 16px",
                          color: "var(--text-primary)", fontSize: 13.5, lineHeight: 1.7,
                          marginBottom: 8,
                          whiteSpace: "pre-line",
                        }}>
                          {lines.join("\n")}
                        </div>
                      )}

                      {/* Barra inferior compacta — sempre visível */}
                      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
                        <span style={{ fontSize: "var(--fs-xs)", color: "var(--text-0-40)" }}>
                          Gerado em {fmtDt(lastTs)} · {entry.weeks} semanas analisadas
                        </span>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button
                            onClick={() => setInsightExpanded(v => !v)}
                            style={{
                              background: "none", border: "none", cursor: "pointer",
                              color: "var(--feedback-warning)", fontSize: "var(--fs-xs)", fontWeight: 600,
                              display: "flex", alignItems: "center", gap: 4, padding: 0,
                            }}
                          >
                            <FontAwesomeIcon icon={insightExpanded ? faChevronUp : faChevronDown} style={{ fontSize: "var(--fs-xs)" }} />
                            {insightExpanded ? "Recolher" : "Ver tudo"}
                          </button>
                          <button
                            onClick={openHistory}
                            style={{
                              background: "none", border: "none", cursor: "pointer",
                              color: "var(--feedback-warning)", fontSize: "var(--fs-xs)", fontWeight: 600,
                              display: "flex", alignItems: "center", gap: 4, padding: 0,
                            }}
                          >
                            <FontAwesomeIcon icon={faHistory} style={{ fontSize: "var(--fs-xs)" }} />
                            Histórico
                          </button>
                        </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            );
          })()}


          <KpiGrid period={profileA.period} bodyProgressEntries={bodyProgressA} measurements={measurementsA} />

          {/* ══ SEÇÃO: Consistência ══════════════════════════════════════════ */}
          <CollapsibleSection title="Consistência" icon={<FontAwesomeIcon icon={faCalendarCheck} />}>
            <div className={styles.sectionLabel} style={{ marginTop: 14 }}>Atividade - ultimas 13 semanas</div>
            <ActivityHeatmap heatmap={profileA.heatmap} />
            <div className={styles.chartGrid2}>
              <div className={styles.chartCard}>
                <div className={styles.chartTitle}>Cadencia semanal</div>
                <div className={styles.chartArea}><Line data={cadenceChart} options={getChartDefaults()} /></div>
              </div>
              <div className={styles.chartCard}>
                <div className={styles.chartTitle}>Dia da semana preferido</div>
                <div className={styles.chartArea}><Bar data={weekdayChart} options={getChartDefaults()} /></div>
              </div>
            </div>
            <div className={styles.chartCard}>
              <div className={styles.chartTitle}>Horário preferido de treino</div>
              <TimeChips byHour={profileA.by_hour || []} />
            </div>
          </CollapsibleSection>

          {/* ══ SEÇÃO: Volume & Carga ════════════════════════════════════════ */}
          <CollapsibleSection title="Volume & Carga" icon={<FontAwesomeIcon icon={faDumbbell} />}>
            <div className={styles.chartGrid2} style={{ marginTop: 14 }}>
              <div className={styles.chartCard}>
                <div className={styles.chartTitle}>Carga total levantada por semana</div>
                <div className={styles.chartArea}>
                  {volumeChart.labels?.length > 0
                    ? <Line data={volumeChart} options={getChartDefaults()} />
                    : <div className={styles.noData}>Sem dados no período</div>}
                </div>
                <div style={{ display: "flex", gap: 24, marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border-0-08)" }}>
                  <div>
                    <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)", marginBottom: 2 }}>Total de séries</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1 }}>
                      {(profileA?.total_sets ?? 0).toLocaleString("pt-BR")}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)", marginBottom: 2 }}>Total de repetições</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1 }}>
                      {(profileA?.total_reps ?? 0).toLocaleString("pt-BR")}
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.chartCard}>
                <div className={styles.chartTitle}>Volume por grupo muscular</div>
                <div className={styles.chartArea}>
                  {profileA?.volume_by_muscle_group?.length > 0 ? (() => {
                    const totalVol = profileA.volume_by_muscle_group.reduce((s, d) => s + d.volume, 0) || 1;
                    const COLORS = ["#4f8ef7","#38bdf8","#facc15","#fb923c","#a78bfa","#34d399","#f472b6"];
                    return (
                      <Bar
                        data={{
                          labels: profileA.volume_by_muscle_group.map(d => {
                            const n = d.muscle_group;
                            const pct = Math.round((d.volume / totalVol) * 100);
                            return `${n.charAt(0).toUpperCase() + n.slice(1)} ${pct}%`;
                          }),
                          datasets: [{
                            label: "Volume (kg)",
                            data: profileA.volume_by_muscle_group.map(d => d.volume),
                            backgroundColor: COLORS.slice(0, profileA.volume_by_muscle_group.length),
                            borderRadius: 4,
                          }]
                        }}
                        options={{
                          ...getChartDefaults(),
                          plugins: {
                            ...getChartDefaults().plugins,
                            tooltip: {
                              callbacks: {
                                label: (ctx) => {
                                  const v = ctx.parsed.y;
                                  const pct = Math.round((v / totalVol) * 100);
                                  return `${v >= 1000 ? (v/1000).toFixed(1) + " Ton" : v.toFixed(1) + " kg"} (${pct}%)`;
                                }
                              }
                            }
                          }
                        }}
                      />
                    );
                  })() : (
                    <p style={{ color: "var(--text-secondary)", fontSize: 13, textAlign: "center", marginTop: 24 }}>
                      Sem dados no período
                    </p>
                  )}
                </div>
              </div>
            </div>
            <LoadProgressionSection data={profileA.exercise_load_progression || []} />
          </CollapsibleSection>

          {/* ══ SEÇÃO: Aderência à prescrição (ordem de execução x prescrita) ═════ */}
          <OrderAdherenceSection
            studentId={primaryId}
            Section={CollapsibleSection}
            onOpenSession={(sessionId) => setSessionDetailId({ sessionId })}
          />

          {/* ══ SEÇÃO: Cardio ════════════════════════════════════════════════ */}
          {(profileA?.cardio_total_sessions > 0) && (
            <CollapsibleSection title="Cardio" icon={<FontAwesomeIcon icon={faPersonRunning} />} defaultOpen={false}>
              <div className={styles.chartGrid2} style={{ marginTop: 14 }}>
                <div className={styles.chartCard}>
                  <div className={styles.chartTitle}>Minutos de cardio por semana</div>
                  <div className={styles.chartArea}>
                    {cardioChart.labels?.length > 0
                      ? <Bar data={cardioChart} options={getChartDefaults()} />
                      : <div className={styles.noData}>Sem dados no período</div>}
                  </div>
                  <div style={{ display: "flex", gap: 24, marginTop: 12, paddingTop: 10, borderTop: "1px solid var(--border-0-08)" }}>
                    <div>
                      <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)", marginBottom: 2 }}>Sessões no período</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1 }}>
                        {profileA.cardio_total_sessions}
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-secondary)", marginBottom: 2 }}>Total de minutos</div>
                      <div style={{ fontSize: 22, fontWeight: 700, color: "var(--text-primary)", lineHeight: 1 }}>
                        {profileA.cardio_total_min}
                      </div>
                    </div>
                  </div>
                </div>
                <div className={styles.chartCard}>
                  <div className={styles.chartTitle}>Exercícios mais frequentes</div>
                  <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 8 }}>
                    {(profileA?.cardio_top_exercises || []).map((ex, i) => {
                      const maxCount = profileA.cardio_top_exercises[0]?.count || 1;
                      return (
                        <div key={i}>
                          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
                            <span style={{ color: "var(--text-primary)" }}>{ex.name}</span>
                            <span style={{ color: "var(--text-secondary)" }}>{ex.count}×</span>
                          </div>
                          <div style={{ height: 5, background: "var(--border-0-10)", borderRadius: 3 }}>
                            <div style={{
                              height: "100%",
                              width: `${Math.round((ex.count / maxCount) * 100)}%`,
                              background: TEAL,
                              borderRadius: 3,
                              transition: "width 0.4s",
                            }} />
                          </div>
                        </div>
                      );
                    })}
                    {!profileA?.cardio_top_exercises?.length && (
                      <p style={{ fontSize: 13, color: "var(--text-secondary)", textAlign: "center", marginTop: 16 }}>
                        Sem dados no período
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </CollapsibleSection>
          )}

          {/* ══ SEÇÃO: Localização dos treinos ═══════════════════════════════ */}
          {(profileA?.locations?.length > 0) && (
            <CollapsibleSection title="Localização dos treinos" icon={<FontAwesomeIcon icon={faLocationDot} />} defaultOpen={false}>
              <StudentLocationsMap locations={profileA.locations} />
            </CollapsibleSection>
          )}

          {/* ══ SEÇÃO: Perfil do Aluno ═══════════════════════════════════════ */}
          <CollapsibleSection title="Perfil do Aluno" icon={<FontAwesomeIcon icon={faUser} />} defaultOpen={false}>
            <div style={{ marginTop: 8 }}>
              <BodyMeasurementsPanel measurements={measurementsA} bodyProgressEntries={bodyProgressA} />
              {bodyProgressA.length >= 2 && (
                <div className={styles.recentWrap} style={{ marginBottom: 14 }}>
                  <div className={styles.sectionLabel}>Evolução de peso</div>
                  <WeightSparkline entries={bodyProgressA} />
                </div>
              )}
              <div className={styles.recentWrap}>
                <div className={styles.measHeader}>
                  <div className={styles.sectionLabel} style={{ margin: 0 }}>Fotos de Progresso</div>
                  <button className={styles.measToggle} onClick={() => setShowStudentPhotos(true)}>
                    <FontAwesomeIcon icon={faCamera} /> Acessar fotos
                  </button>
                </div>
              </div>
            </div>
          </CollapsibleSection>

          {/* ══ SEÇÃO: Comunicação ══════════════════════════════════════════ */}
          <CollapsibleSection title="Comunicação" icon={<FontAwesomeIcon icon={faCommentDots} />} defaultOpen={false}>
            <div style={{ marginTop: 8 }}>
              <StudentReportsPanel studentId={primaryId} onReportRead={onReportRead} />
              <StudentDietPanel
                studentId={primaryId}
                onOpenDiet={() => setOpenNutrition(true)}
              />
            </div>
          </CollapsibleSection>

          {/* ══ SEÇÃO: Sessões ══════════════════════════════════════════════ */}
          <CollapsibleSection title="Sessões" icon={<FontAwesomeIcon icon={faPersonRunning} />} defaultOpen={false}>
            <div style={{ marginTop: 8 }}>
              <div style={{ margin: "0 24px 14px" }}>
                <button
                  onClick={() => setShowWorkoutHistory(true)}
                  style={{
                    width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
                    padding: "10px 16px", borderRadius: 12,
                    background: "var(--surface-secondary)", border: "1px solid var(--border-0-08)",
                    color: "var(--text-0-60)", fontSize: 13, cursor: "pointer",
                    fontWeight: 600, transition: "background .15s",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = "var(--surface-hover)"; e.currentTarget.style.color = "var(--text-primary)"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "var(--surface-secondary)"; e.currentTarget.style.color = "var(--text-0-60)"; }}
                >
                  <FontAwesomeIcon icon={faClockRotateLeft} style={{ fontSize: 13 }} />
                  Histórico completo de treinos
                </button>
              </div>
              <RecentSessions
                recent={profileA.recent}
                studentId={primaryId}
                onSessionClick={(sessionId, checkin) => setSessionDetailId({ sessionId, checkin })}
                onDeleteCheckin={async (checkinId, uid) => {
                  const res = await api.delete(`/users/${uid}/checkins/${checkinId}`);
                  const pts = res?.data?.points_reversed || 0;
                  if (pts > 0) {
                    console.info(`Check-in excluído. ${pts} pts estornados.`);
                  }
                  await loadStudentData(primaryId);
                }}
              />
            </div>
          </CollapsibleSection>
        </div>
      )}
            {primaryId && !profileA && !error && <div className={styles.loadingState}>Carregando dados do aluno…</div>}
          </div>{/* /detailContent */}
        </div>{/* /detailPane */}
      </div>{/* /sidebarLayout */}
    </div>
  );

  const sessionModal = (
    <SessionDetailModal
      sessionId={sessionDetailId?.sessionId}
      checkin={sessionDetailId?.checkin}
      onClose={() => setSessionDetailId(null)}
    />
  );

  const nutritionModal = (
    <NutritionModal
      open={openNutrition}
      onClose={() => setOpenNutrition(false)}
      currentUser={{ role: "professor" }}
      initialStudentId={primaryId}
    />
  );

  const bodyProgressModal = (
    <BodyProgressModal
      open={showStudentPhotos}
      onClose={() => setShowStudentPhotos(false)}
      apiBase={apiUrl}
      studentId={primaryId}
    />
  );

  const workoutHistoryModal = (
    <WorkoutHistoryModal
      open={showWorkoutHistory}
      onClose={() => setShowWorkoutHistory(false)}
      studentId={primaryId}
      studentName={profileA?.student?.name}
    />
  );

  const sendPushModal = (
    <SendPushModal
      open={showSendPush}
      onClose={() => setShowSendPush(false)}
      studentId={primaryId}
      studentName={profileA?.student?.name}
    />
  );

  const insightConfirmModal = showInsightConfirm ? createPortal(
    (() => {
      const { lastTs } = getInsightsMeta(primaryId);
      const entry = aiInsights[primaryId];
      const fmtDt = (d) => d ? d.toLocaleDateString("pt-BR", { day:"2-digit", month:"2-digit", year:"2-digit", hour:"2-digit", minute:"2-digit" }) : "";
      const isRegen = !!entry;
      return (
        <div
          onClick={() => setShowInsightConfirm(false)}
          style={{ position:"fixed", inset:0, zIndex:4000, background:"rgba(0,0,0,0.72)",
            display:"flex", alignItems:"center", justifyContent:"center", padding:"20px" }}
        >
          <div onClick={e => e.stopPropagation()} style={{
            background:"var(--surface-primary)", border:"1px solid var(--border-0-10)",
            borderRadius:16, padding:"28px 28px 24px",
            maxWidth:420, width:"100%", boxShadow:"0 20px 60px rgba(0,0,0,0.6)",
          }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18 }}>
              <FontAwesomeIcon icon={faWandMagicSparkles} style={{ color:"var(--feedback-warning)", fontSize:18 }} />
              <span style={{ fontWeight:700, fontSize:17, color:"var(--text-primary)" }}>
                {isRegen ? "Regenerar Insights IA" : "Gerar Insights IA"}
              </span>
            </div>

            <div style={{ fontSize:13.5, color:"var(--text-0-70)", lineHeight:1.65, marginBottom:20 }}>
              <p style={{ margin:"0 0 10px" }}>
                A IA vai analisar <strong style={{ color:"var(--text-primary)" }}>{insightsWeeks} semanas</strong> de histórico
                e gerar insights acionáveis sobre este aluno.
              </p>
              {isRegen && (
                <p style={{ margin:"0 0 12px" }}>
                  O insight atual (gerado em <strong style={{ color:"var(--text-primary)" }}>{fmtDt(lastTs)}</strong>) será substituído.
                </p>
              )}
              <div style={{
                background:"rgba(251,191,36,0.08)", border:"1px solid rgba(251,191,36,0.22)",
                borderRadius:10, padding:"10px 14px",
                display:"flex", alignItems:"flex-start", gap:9,
              }}>
                <FontAwesomeIcon icon={faLightbulb} style={{ color:"var(--feedback-warning)", fontSize:13, marginTop:2, flexShrink:0 }} />
                <span>
                  <strong style={{ color:"var(--feedback-warning)" }}>Limite:</strong> 1 geração por aluno a cada 7 dias.
                  {!lastTs && " Após gerar, a próxima estará disponível em 7 dias."}
                </span>
              </div>
            </div>

            <div style={{ display:"flex", gap:10, justifyContent:"flex-end" }}>
              <button
                onClick={() => setShowInsightConfirm(false)}
                style={{
                  padding:"8px 18px", borderRadius:9, border:"1px solid var(--border-0-15)",
                  background:"transparent", color:"var(--text-0-60)",
                  fontWeight:600, fontSize:13, cursor:"pointer",
                }}
              >
                Cancelar
              </button>
              <button
                onClick={() => { setShowInsightConfirm(false); handleGenerateInsights(primaryId); }}
                style={{
                  padding:"8px 22px", borderRadius:9, border:"none",
                  background:"var(--accent)",
                  color:"var(--on-accent)", fontWeight:700, fontSize:13, cursor:"pointer",
                  boxShadow:"0 3px 12px rgba(var(--accent-rgb),0.35)",
                }}
              >
                <FontAwesomeIcon icon={faWandMagicSparkles} style={{ marginRight:7 }} />
                {isRegen ? "Regenerar" : "Gerar agora"}
              </button>
            </div>
          </div>
        </div>
      );
    })(),
    document.body
  ) : null;

  const insightHistoryModal = showInsightHistory ? createPortal(
    <div
      onClick={() => setShowInsightHistory(false)}
      style={{
        position: "fixed", inset: 0, zIndex: 3000,
        background: "var(--border-0-65)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: "var(--surface-secondary)",
          border: "1px solid var(--border-0-10)",
          borderRadius: 12,
          width: "100%", maxWidth: 480,
          maxHeight: "80vh",
          display: "flex", flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 16px 12px",
          borderBottom: "1px solid var(--border-0-08)",
        }}>
          <span style={{ color: "var(--text-primary)", fontWeight: 700, fontSize: 14 }}>
            Histórico de Insights IA
          </span>
          <button
            onClick={() => setShowInsightHistory(false)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              color: "var(--text-0-50)", fontSize: 16, padding: 0,
              lineHeight: 1,
            }}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
        <div style={{ overflowY: "auto", padding: "8px 0" }}>
          {insightHistoryLoading && (
            <div style={{ color: "var(--text-0-50)", fontSize: 13, padding: "16px", textAlign: "center" }}>
              Carregando…
            </div>
          )}
          {!insightHistoryLoading && insightHistory.length === 0 && (
            <div style={{ color: "var(--text-0-50)", fontSize: 13, padding: "16px", textAlign: "center" }}>
              Nenhum insight registrado.
            </div>
          )}
          {!insightHistoryLoading && insightHistory.map((item) => (
            <InsightHistoryRow key={item.id} item={item} defaultOpen={insightHistory.length === 1} />
          ))}
        </div>
      </div>
    </div>,
    document.body
  ) : null;

  if (inline) return <>{content}{sessionModal}{nutritionModal}{bodyProgressModal}{workoutHistoryModal}{sendPushModal}{insightHistoryModal}{insightConfirmModal}</>;
  return createPortal(
    <>
      <div className={styles.overlay} onClick={onClose}>{content}</div>
      {sessionModal}
      {nutritionModal}
      {bodyProgressModal}
      {workoutHistoryModal}
      {sendPushModal}
      {insightHistoryModal}
      {insightConfirmModal}
    </>,
    document.body
  );
}
